/**
 * Logger - Application Logging Utility
 *
 * Provides structured, environment-aware logging with support for:
 * - Log levels (debug, info, warn, error)
 * - Module/context tagging
 * - Timestamps
 * - Environment-based filtering
 * - Colored console output in development
 */

// ============================================================================
// Types
// ============================================================================

export type LogLevel = "debug" | "info" | "warn" | "error" | "silent";

export interface LoggerConfig {
    /** Minimum log level to display */
    level: LogLevel;
    /** Enable timestamps in log output */
    showTimestamp: boolean;
    /** Enable colored output (development only) */
    useColors: boolean;
    /** Module/context name for this logger instance */
    module?: string;
}

export interface LogEntry {
    level: LogLevel;
    message: string;
    module?: string;
    timestamp: Date;
    data?: unknown;
}

// ============================================================================
// Constants
// ============================================================================

const LOG_LEVEL_PRIORITY: Record<LogLevel, number> = {
    debug: 0,
    info: 1,
    warn: 2,
    error: 3,
    silent: 4,
};

const LOG_LEVEL_COLORS: Record<Exclude<LogLevel, "silent">, string> = {
    debug: "\x1b[36m", // Cyan
    info: "\x1b[32m",  // Green
    warn: "\x1b[33m",  // Yellow
    error: "\x1b[31m", // Red
};

const RESET_COLOR = "\x1b[0m";

// ============================================================================
// Default Configuration
// ============================================================================

const getDefaultConfig = (): LoggerConfig => ({
    // In production, only show warnings and errors
    // In development, show all logs
    level: import.meta.env.PROD ? "warn" : "debug",
    showTimestamp: import.meta.env.DEV,
    useColors: import.meta.env.DEV,
});

// ============================================================================
// Logger Class
// ============================================================================

class Logger {
    private config: LoggerConfig;

    constructor(config: Partial<LoggerConfig> = {}) {
        this.config = { ...getDefaultConfig(), ...config };
    }

    /**
     * Create a child logger with a specific module name
     */
    child(module: string): Logger {
        return new Logger({ ...this.config, module });
    }

    /**
     * Update logger configuration
     */
    configure(config: Partial<LoggerConfig>): void {
        this.config = { ...this.config, ...config };
    }

    /**
     * Check if a log level should be displayed
     */
    private shouldLog(level: LogLevel): boolean {
        return LOG_LEVEL_PRIORITY[level] >= LOG_LEVEL_PRIORITY[this.config.level];
    }

    /**
     * Format the log prefix (timestamp, level, module)
     */
    private formatPrefix(level: Exclude<LogLevel, "silent">): string {
        const parts: string[] = [];

        // Timestamp
        if (this.config.showTimestamp) {
            const now = new Date();
            const time = now.toLocaleTimeString("en-US", { hour12: false });
            const ms = now.getMilliseconds().toString().padStart(3, "0");
            parts.push(`[${time}.${ms}]`);
        }

        // Level
        const levelTag = level.toUpperCase().padEnd(5);
        if (this.config.useColors) {
            parts.push(`${LOG_LEVEL_COLORS[level]}${levelTag}${RESET_COLOR}`);
        } else {
            parts.push(levelTag);
        }

        // Module
        if (this.config.module) {
            parts.push(`[${this.config.module}]`);
        }

        return parts.join(" ");
    }

    /**
     * Core logging method
     */
    private log(level: Exclude<LogLevel, "silent">, message: string, data?: unknown): void {
        if (!this.shouldLog(level)) return;

        const prefix = this.formatPrefix(level);
        const consoleMethod = level === "debug" ? "log" : level;

        if (data !== undefined) {
            console[consoleMethod](prefix, message, data);
        } else {
            console[consoleMethod](prefix, message);
        }
    }

    /**
     * Debug level - detailed information for debugging
     */
    debug(message: string, data?: unknown): void {
        this.log("debug", message, data);
    }

    /**
     * Info level - general information
     */
    info(message: string, data?: unknown): void {
        this.log("info", message, data);
    }

    /**
     * Warn level - warning messages
     */
    warn(message: string, data?: unknown): void {
        this.log("warn", message, data);
    }

    /**
     * Error level - error messages
     */
    error(message: string, data?: unknown): void {
        this.log("error", message, data);
    }

    /**
     * Log a table (useful for debugging arrays/objects)
     */
    table(data: unknown): void {
        if (!this.shouldLog("debug")) return;
        console.table(data);
    }

    /**
     * Log with timing - useful for performance debugging
     */
    time(label: string): void {
        if (!this.shouldLog("debug")) return;
        console.time(label);
    }

    timeEnd(label: string): void {
        if (!this.shouldLog("debug")) return;
        console.timeEnd(label);
    }

    /**
     * Group related logs
     */
    group(label: string): void {
        if (!this.shouldLog("debug")) return;
        console.group(label);
    }

    groupEnd(): void {
        if (!this.shouldLog("debug")) return;
        console.groupEnd();
    }

    /**
     * Collapsed group (expandable in console)
     */
    groupCollapsed(label: string): void {
        if (!this.shouldLog("debug")) return;
        console.groupCollapsed(label);
    }
}

// ============================================================================
// Singleton Instances
// ============================================================================

/** Root logger instance */
export const logger = new Logger();

/** Pre-configured loggers for common modules */
export const httpLogger = logger.child("HTTP");
export const authLogger = logger.child("Auth");
export const appLogger = logger.child("App");

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Create a logger for a specific module
 */
export const createLogger = (module: string): Logger => {
    return logger.child(module);
};

/**
 * Disable all logging (useful for tests)
 */
export const silentLogger = (): void => {
    logger.configure({ level: "silent" });
};

/**
 * Enable verbose logging (useful for debugging)
 */
export const verboseLogger = (): void => {
    logger.configure({ level: "debug" });
};

export default logger;
